import * as React from 'react'
import type {
  IrrigationEstimateDto,
  IrrigationSiteDto,
} from '@lib/dto/irrigation.dto'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import { formatMoneyValue, parseMoney } from '@lib/helpers/money'
import { taxOnPortion } from '@lib/helpers/tax'
import {
  EMPTY_SELECTION,
  documentTaxRate,
  priceSelection,
} from '@lib/helpers/proposal-selection'
import { useRepairEstimate } from '@/irrigation-survey/RepairEstimate'
import { useBid, type BidContext } from './useBid'
import type { OtherSites } from './useTreeBidTotal'

export interface IrrigationBidTotal {
  bidContext: BidContext
  /** This site's open repairs as the server last priced them. */
  estimate: IrrigationEstimateDto | null
  isLoading: boolean
  /** The price is out of reach for now: no signal, or not synced yet. */
  isUnavailable: boolean
  /** When the server last priced this site's repairs, epoch milliseconds. */
  pricedAt: number | null
  others: OtherSites
  subtotalCents: number | null
  taxRatePercent: string | null
  taxCents: number | null
  totalCents: number | null
  /** Some repairs cannot be priced yet, so the total is a floor. */
  isIncomplete: boolean
  /** This site's figure alone, which the technician's edits move. */
  siteCents: number
}

interface Args {
  siteId: string
  site: IrrigationSiteDto
  /** The visit the repairs are in has reached the server. */
  canPrice: boolean
  organization: OrganizationDto | undefined
  requestedProposalId: string | null
}

/**
 * What the irrigation on a bid comes to, while the survey is being edited.
 *
 * Irrigation is priced from the office's copy of Housecall Pro, so unlike the
 * trees this cannot be worked out on the phone: it is the server's figure for
 * what has synced, and it says when it was priced. The bid's other sites come
 * from the draft, and repairs are never optional, so the figure is simply
 * every repair the draft bids.
 */
export function useIrrigationBidTotal(args: Args): IrrigationBidTotal {
  const bidContext = useBid(args.siteId, 'irrigation', args.requestedProposalId)
  const { bid, document } = bidContext
  const estimate = useRepairEstimate(args.site, args.canPrice)

  const otherSites = React.useMemo(() => {
    if (bid === null || document.data === undefined) return null
    const names = new Map(
      bid.version.sites.map((site) => [site.name, site.siteId]),
    )
    const outcome = priceSelection(document.data, EMPTY_SELECTION)
    return {
      sites: outcome.irrigationSites
        .filter((site) => names.get(site.siteName) !== args.siteId)
        .map((site) => ({
          siteId: names.get(site.siteName) ?? null,
          name: site.siteName,
          cents: parseMoney(site.amount) ?? 0,
          taxableCents: parseMoney(site.taxable) ?? 0,
          incomplete: false,
        })),
      taxRatePercent: documentTaxRate(document.data),
    }
  }, [bid, document.data, args.siteId])

  function othersOf(): OtherSites {
    if (bid === null) return { state: 'none' }
    if (bid.version.sites.length <= 1) return { state: 'ready', sites: [] }
    if (otherSites !== null) return { state: 'ready', sites: otherSites.sites }
    return document.isError ? { state: 'unavailable' } : { state: 'loading' }
  }
  const others = othersOf()

  const data = estimate.data ?? null
  const siteCents = parseMoney(data?.amount ?? null) ?? 0
  const siteTaxable = parseMoney(data?.taxable ?? null) ?? 0
  const readyOthers = others.state === 'ready' ? (otherSites?.sites ?? []) : []
  const subtotal =
    siteCents + readyOthers.reduce((sum, site) => sum + site.cents, 0)
  const taxable =
    siteTaxable + readyOthers.reduce((sum, site) => sum + site.taxableCents, 0)
  const hasAnything = data !== null || readyOthers.length > 0
  const subtotalCents = hasAnything ? subtotal : null
  const taxRatePercent =
    otherSites?.taxRatePercent ?? args.organization?.taxRatePercent ?? null
  const taxed = taxOnPortion(
    subtotalCents === null ? null : formatMoneyValue(subtotalCents),
    formatMoneyValue(taxable),
    taxRatePercent,
  )

  return {
    bidContext,
    estimate: data,
    isLoading: args.canPrice && estimate.isPending,
    isUnavailable: data === null && (!args.canPrice || estimate.isError),
    pricedAt: data === null ? null : estimate.dataUpdatedAt,
    others,
    subtotalCents,
    taxRatePercent,
    taxCents: parseMoney(taxed.taxAmount),
    totalCents: parseMoney(taxed.amountDue),
    isIncomplete: (data?.unpriceable.length ?? 0) > 0,
    siteCents,
  }
}
